import { css } from '@emotion/react';
import { FC } from 'react';
import { useQuery } from 'react-query';
import { Link, useRouteMatch } from 'react-router-dom';
import { format, parseISO, isFuture } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { colors, fontStyles, spacing } from '../../../styles';
import SectionHeading from '../SectionHeading';
import SkeletonBox from '../../SkeletonBox';
import ErrorSection from '../../ErrorSection';
import { getEarningsTranscripts, EarningsTranscriptSummary } from '../../../apis/backend';

interface Props {
  symbol: string;
}

const getSentimentIndicator = (sentiment: 'positive' | 'neutral' | 'negative' | null): string => {
  switch (sentiment) {
    case 'positive':
      return '🟢';
    case 'negative':
      return '🔴';
    case 'neutral':
      return '';
    default:
      return '';
  }
};

const formatFiscalDate = (fiscalDate: string): string => {
  try {
    const date = parseISO(fiscalDate);
    return format(date, 'M/d/yy');
  } catch {
    return fiscalDate;
  }
};

const formatEarningsDate = (earningsDate: string): string => {
  try {
    const date = parseISO(earningsDate);
    return format(date, 'MMMM d, yyyy');
  } catch {
    return earningsDate;
  }
};

const truncateToFirstSentence = (text: string): string => {
  if (!text) return text;

  // Truncate at markdown headers (##) before looking for sentence endings
  const headerIndex = text.indexOf('##');
  if (headerIndex > 0) {
    const beforeHeader = text.substring(0, headerIndex).trim();
    if (beforeHeader) return beforeHeader;
  }

  // Find the first sentence by looking for periods, exclamation marks, or question marks
  // followed by a space and a capital letter, or at the end of the string
  const firstSentenceMatch = text.match(/^[^.!?]*[.!?](?=\s+[A-Z]|$)/);

  if (firstSentenceMatch) {
    return firstSentenceMatch[0] + '...';
  }

  // If no clear sentence ending is found, truncate at around 100 characters
  if (text.length > 100) {
    return text.substring(0, 100).trim() + '...';
  }

  return text;
};

const EarningsTranscripts: FC<Props> = ({ symbol }) => {
  const { t, i18n } = useTranslation();
  const match = useRouteMatch();

  const { data, isLoading, error, refetch } = useQuery(['investments', symbol, 'earnings', i18n.language], () =>
    getEarningsTranscripts(symbol),
  );

  return (
    <div>
      <SectionHeading text={t('symbol.earningsTranscripts')} showProBadge />

      {isLoading && (
        <div>
          <div
            css={css`
              margin-bottom: ${spacing.deci};
            `}
          >
            <SkeletonBox width="100%" />
          </div>
          <div
            css={css`
              margin-bottom: ${spacing.deci};
            `}
          >
            <SkeletonBox width="100%" />
          </div>
          <div
            css={css`
              margin-bottom: ${spacing.deci};
            `}
          >
            <SkeletonBox width="100%" />
          </div>
        </div>
      )}
      {error && <ErrorSection text={t('symbol.earningsTranscriptsError')} onRetry={() => refetch()} />}
      {data && (
        <div>
          {data.map((transcript: EarningsTranscriptSummary) => {
            const [yearStr, quarterStr] = transcript.quarter.split('Q');
            const year = parseInt(yearStr, 10);
            const quarterNumber = parseInt(quarterStr, 10);
            const isUpcoming = isFuture(parseISO(transcript.earningsDate));
            const sentimentEmoji = getSentimentIndicator(transcript.sentiment);

            // Format the title based on whether it's upcoming or past
            let title: string;
            if (isUpcoming) {
              const earningsDateFormatted = format(parseISO(transcript.earningsDate), 'MMMM d');
              title = t('symbol.nextEarnings', { date: earningsDateFormatted });
            } else {
              const fiscalDateFormatted = transcript.fiscalDate ? formatFiscalDate(transcript.fiscalDate) : '';
              title = t('symbol.quarterEnding', { date: fiscalDateFormatted });
            }

            return (
              <div
                key={`${year}-Q${quarterNumber}`}
                css={css`
                  margin-bottom: ${spacing.regular};
                `}
              >
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: ${spacing.centi};
                  `}
                >
                  {isUpcoming ? (
                    <span
                      css={css`
                        color: ${colors.liquorice};
                        ${fontStyles.roboCat};
                        display: flex;
                        align-items: center;
                        gap: ${spacing.centi};

                        html[data-theme='dark'] & {
                          color: ${colors.sugar};
                        }
                      `}
                    >
                      {title} {sentimentEmoji}
                    </span>
                  ) : (
                    <Link
                      to={`${match.url}/transcript?year=${year}&quarter=${quarterNumber}${transcript.summary ? `&summary=${encodeURIComponent(transcript.summary)}` : ''}`}
                      css={css`
                        color: ${colors.petrolBlue};
                        text-decoration: none;
                        ${fontStyles.roboCat};
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        gap: ${spacing.centi};

                        html[data-theme='dark'] & {
                          color: ${colors.skyBlue};
                        }

                        &:hover {
                          text-decoration: underline;
                        }
                      `}
                    >
                      <span>
                        {title} {sentimentEmoji}
                      </span>
                    </Link>
                  )}

                  {!isUpcoming && (
                    <span
                      css={css`
                        color: ${colors.tungsten};
                        ${fontStyles.cat};
                        font-size: 14px;

                        html[data-theme='dark'] & {
                          color: ${colors.sugar};
                        }
                      `}
                    >
                      {formatEarningsDate(transcript.earningsDate)}
                    </span>
                  )}
                </div>

                {transcript.summary && (
                  <p
                    css={css`
                      margin: ${spacing.centi} 0;
                      color: ${colors.tungsten};
                      ${fontStyles.cat};

                      html[data-theme='dark'] & {
                        color: ${colors.sugar};
                      }
                    `}
                  >
                    {truncateToFirstSentence(transcript.summary)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EarningsTranscripts;
